import { useState } from 'react'
import type { CSSProperties } from 'react'
import { useLocale } from '../i18n/useLocale'
import {
  eligibilityReport, markEligibilityReported, openSupportForm, type MismatchReason,
} from '../data/support'
import { showNotWiredToast } from '../lib/notWiredToast'

/*
 * Eligibility-mismatch banner + the "always an out" (Duncan rulings
 * BOARDROOM-2026-08-29-120259 #2/#3 + ADDENDUM 122136 + ADDENDUM 2 123126).
 * ONE component serves BOTH surfaces — the get-ready event card and the
 * profile wizard's inline fail-fast (gender AND age axes) — so the two
 * sub-states behave identically everywhere:
 *
 *  1. BEFORE reporting: amber banner carrying the mismatch copy + the button
 *     "Submit a support ticket so we can help you resolve this" (verbatim per
 *     ruling 2). It opens the in-app support form (CMT-2026-10-31-002,
 *     src/data/support.ts) prefilled with the mismatch reason, ticket and
 *     event, so support needs no interrogation.
 *  2. AFTER the support route accepted the message (201 — the ONLY writer of
 *     the record, so a cancelled or failed send never reads as "submitted"):
 *     "You currently don't have a valid ticket … — support request submitted"
 *     + the SUP- reference the route returned, NO button (no re-nag), and a
 *     low-key "Message support again" link that never changes the record.
 *     The record is device-local and keyed per ticket.
 *
 * With no support host configured the click shows the honest not-wired toast
 * and nothing advances.
 */

export function EligibilityMismatchNotice({ ticketId, eventId, eventName, copy, reason, participantName }: {
  ticketId: string
  /** The ticket's event (CMT-2026-10-31-005 b: a ticket entry carries its event). */
  eventId?: string
  eventName: string
  /** Locale-resolved mismatch copy (participant voice, reason-aware). */
  copy: string
  reason: MismatchReason
  participantName?: string
}) {
  const { t, lang } = useLocale()
  // Seeded from the device-local store; a 201 from the support route is the only writer.
  const [report, setReport] = useState(() => eligibilityReport(ticketId))

  const barStyle: CSSProperties = {
    display: 'flex', flexDirection: 'column', gap: 9, padding: '11px 16px',
    background: 'rgba(230,193,90,.1)', borderBlockStart: '1px solid rgba(230,193,90,.28)',
  }
  const copyStyle: CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 8,
    fontSize: 11.5, lineHeight: 1.45, color: 'var(--tru-status-amber, var(--warn))', fontWeight: 700,
  }

  // ONE prefill for the first message AND "message again", so they never drift.
  const prefill = () => {
    const base = t('eligibility.support.prefill', { reason: copy, ticket: ticketId, event: eventName })
    return participantName ? `${base}\nParticipant: ${participantName}` : base
  }

  const onSupport = () => {
    const opened = openSupportForm({
      topic: 'ticket_type',
      eventId,
      message: prefill(),
      onSent: (reference) => setReport(markEligibilityReported(ticketId, reason, reference)),
    })
    if (!opened) showNotWiredToast()
  }

  // Another message never rewrites the recorded report (its reference + date
  // describe the first accepted message).
  const onReopen = () => {
    if (!openSupportForm({ topic: 'ticket_type', eventId, message: prefill() })) showNotWiredToast()
  }

  if (report) {
    const date = new Date(report.at)
    const dateText = Number.isNaN(date.getTime())
      ? report.at
      : date.toLocaleDateString(lang === 'ar' ? 'ar' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    return (
      <div data-testid="elig-mismatch-reported" role="status" style={barStyle}>
        <span style={copyStyle}>
          <span aria-hidden style={{ flex: 'none', fontSize: 13 }}>⚠</span>
          {t('eligibility.support.reported')}
        </span>
        <span data-testid="elig-mismatch-ref" style={{ fontSize: 11, color: 'var(--tru-text-tertiary, var(--text-secondary))' }}>
          {report.reference
            ? t('eligibility.support.reportedSup', { reference: report.reference, date: dateText })
            : t('eligibility.support.reportedRef', { ticket: ticketId, date: dateText })}
        </span>
        <button
          data-testid="elig-mismatch-reopen"
          onClick={onReopen}
          style={{
            alignSelf: 'start', border: 0, background: 'transparent', padding: 0,
            minHeight: 24, fontFamily: 'inherit', fontSize: 11, fontWeight: 600,
            color: 'var(--tru-text-tertiary, var(--text-secondary))',
            textDecoration: 'underline', cursor: 'pointer', textAlign: 'start',
          }}
        >
          {t('eligibility.support.reopen')}
        </button>
      </div>
    )
  }

  // Sub-state 1 — unreported: the mismatch copy + the out.
  return (
    <div data-testid="elig-mismatch-banner" role="alert" style={barStyle}>
      <span style={copyStyle}>
        <span aria-hidden style={{ flex: 'none', fontSize: 13 }}>⚠</span>
        {copy}
      </span>
      <button
        data-testid="elig-mismatch-support-cta"
        onClick={onSupport}
        style={{
          alignSelf: 'start', border: '1px solid rgba(230,193,90,.45)', borderRadius: 9,
          minHeight: 40, padding: '0 14px', background: 'transparent',
          color: 'var(--tru-status-amber, var(--warn))', fontFamily: 'inherit',
          fontSize: 12, fontWeight: 800, cursor: 'pointer', textAlign: 'start',
        }}
      >
        {t('eligibility.support.cta')}
      </button>
    </div>
  )
}
